import React, { useEffect, useMemo, useState } from "react";
import Layout from "../../components/Layout";
import {
  Search,
  Filter,
  MoreHorizontal,
  Eye,
  Edit2,
  Lock,
  Trash,
  Clock,
  X,
} from "lucide-react";
import "./../../styles/member/users.css";
import { UsersService, User } from "../../services/usersService";
import { formatDateTime } from "../../utils/dateFormatter";
import ConfirmDelete from "../../components/ConfirmDelete";

type UiUser = {
  id: number;
  name: string;
  email: string;
  role: string;
  package: string;
  status: string;
  posts: number;
  joined: string;
  lastLogin: string;
};

const Users: React.FC = () => {
  const [actionMenu, setActionMenu] = useState<number | null>(null);
  const [popup, setPopup] = useState<{ type: string; userIdx: number | null }>({
    type: "",
    userIdx: null,
  });
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [statusChanging, setStatusChanging] = useState<boolean>(false);
  const [notif, setNotif] = useState<string>("");
  const [txnLoading, setTxnLoading] = useState<boolean>(false);
  const [txnError, setTxnError] = useState<string>("");
  const [txns, setTxns] = useState<any[]>([]);
  const [txnPage, setTxnPage] = useState<number>(1);
  const [txnTotalPages, setTxnTotalPages] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [rawUsers, setRawUsers] = useState<User[]>([]);

  // Chỉnh sửa trạng thái biểu mẫu
  const [editForm, setEditForm] = useState<{
    fullName: string;
    email: string;
    role: string;
    tokenBalance: number;
    isActive: boolean;
  }>({
    fullName: "",
    email: "",
    role: "",
    tokenBalance: 0,
    isActive: true,
  });
  const [editLoading, setEditLoading] = useState<boolean>(false);
  const [editError, setEditError] = useState<string>("");
  const [editSuccess, setEditSuccess] = useState<string>("");

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [packageFilter, setPackageFilter] = useState("");

  const [roleOpen, setRoleOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [packageOpen, setPackageOpen] = useState(false);

  // Đóng menu thao tác khi click ra ngoài
  useEffect(() => {
    const handleDocumentClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      // Nếu click không nằm trong nút mở menu hoặc menu, thì đóng menu
      const isInsideMenu = target.closest(".action-menu");
      const isTrigger = target.closest(".action-trigger");
      if (!isInsideMenu && !isTrigger) {
        setActionMenu(null);
      }
    };

    document.addEventListener("mousedown", handleDocumentClick);
    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
    };
  }, []);

  const roleOptions = [
    { value: "", label: "Tất cả vai trò" },
    { value: "admin", label: "Admin" },
    { value: "premium", label: "Premium" },
    { value: "moderator", label: "Kiểm duyệt viên" },
    { value: "tool_user", label: "Tool User" },
    { value: "member", label: "Thành viên" },
  ];

  const statusOptions = [
    { value: "", label: "Tất cả trạng thái" },
    { value: "Hoạt động", label: "Hoạt động" },
    { value: "Tạm khoá", label: "Tạm khoá" },
  ];

  const packageOptions = [
    { value: "", label: "Tất cả gói" },
    { value: "basic", label: "Gói Basic" },
    { value: "premium", label: "Gói Premium" },
    { value: "advance", label: "Gói Advance" },
    { value: "super", label: "Gói Super" },
  ];

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError("");
        const data = await UsersService.listUsers();
        if (!mounted) return;
        // Lấy thêm số bài viết cho từng user (song song)
        const counts = await Promise.all(
          data.map((u) => UsersService.getUserPostCount(u.id).catch(() => 0))
        );
        // Gắn tạm vào object để tính UI posts
        const enriched = data.map(
          (u, idx) => ({ ...u, __postCount: counts[idx] } as any)
        );
        setRawUsers(enriched as any);
      } catch (e: any) {
        if (mounted) setError(e?.message || "Không thể tải người dùng");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Fetch transactions khi mở popup history hoặc thay đổi trang
  useEffect(() => {
    if (popup.type !== "history" || popup.userIdx === null) return;
    const user = rawUsers[popup.userIdx];
    if (!user) return;
    let mounted = true;
    (async () => {
      try {
        setTxnLoading(true);
        setTxnError("");
        const res = await UsersService.listUserTransactions(
          user.id,
          txnPage,
          10
        );
        if (!mounted) return;
        setTxns(res.data || []);
        setTxnTotalPages(res.totalPages || 1);
      } catch (e: any) {
        if (mounted)
          setTxnError(e?.message || "Không thể tải lịch sử giao dịch");
      } finally {
        if (mounted) setTxnLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [popup, txnPage, rawUsers]);

  const users: UiUser[] = useMemo(() => {
    return (rawUsers || []).map((u) => {
      const roleLower = String(u.role || "").toLowerCase();
      // Mapping từ Backend (xem backend/shared/enums/package-type.enum.ts và post-limits.service.ts):
      // MEMBER -> FREE (Basic), PREMIUM -> TOKEN100 (Advance), MODERATOR -> TOKEN100 (Advance), ADMIN -> TOKEN1000 (Super)
      const packageName =
        roleLower === "admin" ||
        roleLower === "moderator" ||
        roleLower === "tool_user"
          ? "Vô hạn"
          : roleLower === "member"
          ? "Gói Basic"
          : roleLower === "premium" || roleLower === "moderator"
          ? "Gói Advance"
          : "Gói Basic";
      return {
        id: u.id,
        name: u.profile?.fullName || u.fullName || "(Chưa cập nhật)",
        email: u.email,
        role: u.role,
        package: packageName,
        status: u.isActive ? "Hoạt động" : "Tạm khoá",
        posts: (u as any).__postCount ?? 0,
        joined: formatDateTime(u.createdAt),
        lastLogin: formatDateTime(u.updatedAt),
      };
    });
  }, [rawUsers]);

  // Trình xử lý đóng cửa sổ bật lên
  const closePopup = () => {
    setPopup({ type: "", userIdx: null });
    setEditError("");
    setEditSuccess("");
    setEditForm({
      fullName: "",
      email: "",
      role: "",
      tokenBalance: 0,
      isActive: true,
    });
  };
  const refreshUsers = async () => {
    const updatedUsers = await UsersService.listUsers();
    setRawUsers(updatedUsers);
  };

  const handleToggleLock = async (index: number) => {
    const target = rawUsers[index];
    if (!target) return;
    try {
      setStatusChanging(true);
      setError("");
      const nextActive = !target.isActive;
      await UsersService.updateUserStatus(target.id, nextActive);
      await refreshUsers();
      setNotif(nextActive ? "Đã mở khóa tài khoản" : "Đã tạm khoá tài khoản");
      setTimeout(() => setNotif(""), 2000);
    } catch (e: any) {
      setError(e?.message || "Cập nhật trạng thái thất bại");
    } finally {
      setStatusChanging(false);
    }
  };

  const requestDelete = (index: number) => {
    setPopup({ type: "delete", userIdx: index });
    setConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (popup.userIdx === null) return;
    const target = rawUsers[popup.userIdx];
    if (!target) return;
    try {
      setDeleting(true);
      setError("");
      await UsersService.deleteUser(target.id);
      await refreshUsers();
      setConfirmDeleteOpen(false);
      setPopup({ type: "", userIdx: null });
      setNotif("Đã xoá tài khoản thành công");
      setTimeout(() => setNotif(""), 2000);
    } catch (e: any) {
      setError(e?.message || "Xoá tài khoản thất bại");
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    setConfirmDeleteOpen(false);
    setPopup({ type: "", userIdx: null });
  };

  // Khởi tạo form chỉnh sửa với dữ liệu người dùng
  const initializeEditForm = (user: UiUser, rawUser: any) => {
    setEditForm({
      fullName: user.name || "",
      email: user.email,
      role: user.role,
      tokenBalance: rawUser.tokenBalance || 0,
      isActive: user.status === "Hoạt động",
    });
  };

  // Xử lý gửi form chỉnh sửa
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (popup.userIdx === null) return;

    const user = users[popup.userIdx];
    const rawUser = rawUsers[popup.userIdx];

    setEditLoading(true);
    setEditError("");
    setEditSuccess("");

    try {
      // Kiểm tra form
      if (!editForm.fullName.trim()) {
        setEditError("Tên người dùng không được để trống");
        return;
      }

      if (!editForm.email.trim()) {
        setEditError("Email không được để trống");
        return;
      }

      if (editForm.tokenBalance < 0) {
        setEditError("Số dư token không được âm");
        return;
      }

      // Kiểm tra sự thay đổi vai trò sẽ ảnh hưởng đến số dư token
      if (editForm.role !== user.role) {
        if (editForm.role === "moderator" && user.role !== "moderator") {
          // Người dùng sẽ nhận 100 token khi được nâng lên MODERATOR
        } else if (user.role === "moderator" && editForm.role !== "moderator") {
          // Người dùng sẽ mất 100 token khi bị hạ xuống MODERATOR
        } else if (editForm.role === "tool_user" && user.role !== "tool_user") {
          // Người dùng sẽ có quyền đăng bài không giới hạn khi được nâng lên TOOL_USER
        } else if (user.role === "tool_user" && editForm.role !== "tool_user") {
          // Người dùng sẽ mất quyền đăng bài không giới hạn khi bị hạ xuống khỏi TOOL_USER
        }
      }

      // 1. Cập nhật thông tin cơ bản
      await UsersService.updateUser(rawUser.id, {
        fullName: editForm.fullName.trim(),
        phone: "", // Thêm trường phone nếu cần
        tokenBalance: editForm.tokenBalance,
      });

      // 2. Cập nhật role nếu có thay đổi
      if (editForm.role !== user.role) {
        await UsersService.updateUserRole(rawUser.id, editForm.role as any);
      }

      // 3. Cập nhật status nếu có thay đổi
      const newIsActive = editForm.isActive;
      if (newIsActive !== rawUser.isActive) {
        await UsersService.updateUserStatus(rawUser.id, newIsActive);
      }

      // Làm mới danh sách người dùng
      const updatedUsers = await UsersService.listUsers();
      setRawUsers(updatedUsers);

      // Hiển thị thông báo thành công
      setEditSuccess("Cập nhật thông tin người dùng thành công!");

      // Đóng popup sau một thời gian ngắn
      setTimeout(() => {
        closePopup();
      }, 1500);
    } catch (e: any) {
      setEditError(e?.message || "Cập nhật thất bại");
    } finally {
      setEditLoading(false);
    }
  };

  // Render nội dung popup
  const renderPopup = () => {
    if (popup.userIdx === null) return null;
    const u = users[popup.userIdx];

    // Xem chi tiết người dùng
    if (popup.type === "detail") {
      const rawUser = rawUsers[popup.userIdx];
      return (
        <div className="popup-overlay">
          <div className="popup-card">
            <div className="popup-header">
              <h2 className="popup-title">Chi tiết người dùng</h2>
              <button onClick={closePopup}>
                <X size={28} />
              </button>
            </div>
            <div className="popup-grid">
              <div>
                <div className="popup-label">ID người dùng</div>
                <div className="popup-value">
                  UM{u.id.toString().padStart(3, "0")}
                </div>
                <div className="popup-label">Email</div>
                <div className="popup-value">{u.email}</div>
                <div className="popup-label">Số dư Token</div>
                <div className="popup-value">
                  {rawUser.tokenBalance || 0}{" "}
                  <span role="img" aria-label="coin">
                    🪙
                  </span>
                </div>
                <div className="popup-label">Ngày tạo tài khoản</div>
                <div className="popup-value">{u.joined}</div>
              </div>
              <div>
                <div className="popup-label">Tên người dùng</div>
                <div className="popup-value">{u.name}</div>
                <div className="popup-label">Vai trò</div>
                <div className="popup-value">{u.role}</div>
                <div className="popup-label">Trạng thái</div>
                <span
                  className={`badge status ${
                    u.status === "Hoạt động" ? "active" : "locked"
                  }`}
                >
                  {u.status}
                </span>
                <div className="popup-label">Gói dịch vụ</div>
                <div className="popup-value">{u.package || "Không có"}</div>
                <div className="popup-label">Ngày đăng nhập gần nhất</div>
                <div className="popup-value">{u.lastLogin}</div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Chỉnh sửa người dùng
    if (popup.type === "edit") {
      return (
        <div className="popup-overlay">
          <div className="popup-card">
            <div className="popup-header">
              <h2 className="popup-title">Chỉnh sửa chi tiết</h2>
              <button className="popup-close" onClick={closePopup}>
                <X size={20} />
              </button>
            </div>
            <form className="popup-form" onSubmit={handleEditSubmit}>
              {editError && <div className="error">{editError}</div>}
              {editSuccess && <div className="success">{editSuccess}</div>}
              <div className="form-row">
                <div>
                  <label>Tên người dùng</label>
                  <input
                    type="text"
                    value={editForm.fullName}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        fullName: e.target.value,
                      }))
                    }
                    required
                  />
                </div>
                <div>
                  <label>Email</label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                    disabled
                  />
                </div>
              </div>
              <div className="form-row">
                <div>
                  <label>Vai trò</label>
                  <select
                    value={editForm.role}
                    onChange={(e) =>
                      setEditForm((prev) => ({ ...prev, role: e.target.value }))
                    }
                  >
                    <option value="member">Thành viên</option>
                    <option value="premium">Premium</option>
                    <option value="moderator">Kiểm duyệt viên</option>
                    <option value="tool_user">Tool User</option>
                  </select>
                </div>
                <div>
                  <label>Số dư Token</label>
                  <div className="token-input">
                    <input
                      type="number"
                      value={editForm.tokenBalance}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          tokenBalance: Number(e.target.value),
                        }))
                      }
                      min="0"
                      disabled={
                        editForm.role !== u.role &&
                        (editForm.role === "moderator" ||
                          u.role === "moderator" ||
                          editForm.role === "tool_user" ||
                          u.role === "tool_user")
                      }
                    />
                    <span role="img" aria-label="coin">
                      🪙
                    </span>
                  </div>
                  {editForm.role !== u.role &&
                    (editForm.role === "moderator" ||
                      u.role === "moderator" ||
                      editForm.role === "tool_user" ||
                      u.role === "tool_user") && (
                      <small className="helper-text">
                        {editForm.role === "moderator"
                          ? "Sẽ tự động cộng 100 token"
                          : editForm.role === "tool_user"
                          ? "Sẽ có quyền đăng bài không giới hạn"
                          : u.role === "tool_user"
                          ? "Sẽ mất quyền đăng bài không giới hạn"
                          : "Sẽ tự động trừ 100 token"}
                      </small>
                    )}
                </div>
              </div>
              <div className="form-row">
                <div style={{ width: "100%" }}>
                  <label>Trạng thái</label>
                  <select
                    value={editForm.isActive ? "active" : "inactive"}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        isActive: e.target.value === "active",
                      }))
                    }
                  >
                    <option value="active">Hoạt động</option>
                    <option value="inactive">Tạm khoá</option>
                  </select>
                </div>
              </div>
              <div className="button-container">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={closePopup}
                  disabled={editLoading}
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={editLoading}
                >
                  {editLoading ? "Đang cập nhật..." : "Cập nhật"}
                </button>
              </div>
            </form>
          </div>
        </div>
      );
    }

    // Lịch sử giao dịch người dùng
    if (popup.type === "history") {
      const rawUser = rawUsers[popup.userIdx];
      return (
        <div className="popup-overlay">
          <div className="popup-card">
            <div className="popup-header">
              <h2 className="popup-title">Lịch sử giao dịch</h2>
              <button onClick={closePopup}>
                <X size={28} />
              </button>
            </div>
            <p className="popup-subtitle">
              Giao dịch của {rawUser?.profile?.fullName || rawUser?.fullName || rawUser?.email}
            </p>
            {txnError && <div className="error">{txnError}</div>}
            {txnLoading ? (
              <div className="loading">Đang tải...</div>
            ) : (
              <>
                <table className="popup-table">
                  <thead>
                    <tr>
                      <th>Mã giao dịch</th>
                      <th>Loại</th>
                      <th>Trạng thái</th>
                      <th>Nội dung</th>
                      <th>Ngày giao dịch</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txns.length === 0 && (
                      <tr>
                        <td colSpan={5} style={{ textAlign: "center" }}>
                          Không có giao dịch
                        </td>
                      </tr>
                    )}
                    {txns.map((t, idx) => {
                      const statusClass =
                        t.status === "completed"
                          ? "active"
                          : t.status === "failed"
                          ? "locked"
                          : "pending";
                      const statusLabel =
                        t.status === "completed"
                          ? "Hoàn thành"
                          : t.status === "failed"
                          ? "Thất bại"
                          : t.status === "cancelled"
                          ? "Đã hủy"
                          : "Đang xử lý";
                      return (
                        <tr key={idx}>
                          <td>{t.transactionCode}</td>
                          <td>{t.type}</td>
                          <td>
                            <span className={`badge status ${statusClass}`}>
                              {statusLabel}
                            </span>
                          </td>
                          <td>{t.description || "-"}</td>
                          <td>{formatDateTime(t.createdAt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="pagination">
                  <button
                    disabled={txnPage <= 1}
                    onClick={() => setTxnPage((p) => Math.max(1, p - 1))}
                  >
                    Trước
                  </button>
                  <span>
                    Trang {txnPage}/{txnTotalPages}
                  </span>
                  <button
                    disabled={txnPage >= txnTotalPages}
                    onClick={() =>
                      setTxnPage((p) => Math.min(txnTotalPages, p + 1))
                    }
                  >
                    Sau
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      const matchSearch =
        !search ||
        u.name.toLowerCase().includes(search.toLowerCase()) ||
        u.email.toLowerCase().includes(search.toLowerCase());
      const matchRole =
        !roleFilter || u.role.toLowerCase() === roleFilter.toLowerCase();
      const matchStatus = !statusFilter || u.status === statusFilter;
      const matchPackage =
        !packageFilter ||
        (u.package &&
          u.package.toLowerCase().includes(packageFilter.toLowerCase()));
      return matchSearch && matchRole && matchStatus && matchPackage;
    });
  }, [users, search, roleFilter, statusFilter, packageFilter]);

  return (
    <Layout>
      <div className="users-page">
        {/* Tiêu đề */}
        <h2 className="users-title">Quản lý người dùng</h2>
        <p className="users-subtitle">
          Quản lý tài khoản và quyền hạn người dùng
        </p>

        {/* Bộ lọc người dùng */}
        <div className="filter-box">
          <div className="filter-header">
            <Filter size={24} />
            <span style={{ fontSize: 24, fontWeight: 600 }}>Bộ lọc</span>
          </div>
          <div className="filter-body">
            <div className="search-box">
              <Search size={18} />
              <input
                type="text"
                placeholder="Tìm kiếm tên người dùng"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {/* Vai trò */}
            <div className="filter-dropdown">
              <button
                type="button"
                className="dropdown-trigger"
                onClick={() => setRoleOpen((v) => !v)}
              >
                {roleOptions.find((o) => o.value === roleFilter)?.label ||
                  "Tất cả vai trò"}
                <span className="dropdown-arrow">▼</span>
              </button>
              {roleOpen && (
                <div className="dropdown-menu">
                  {roleOptions.map((opt) => (
                    <div
                      key={opt.value}
                      className={`dropdown-item${
                        roleFilter === opt.value ? " active" : ""
                      }`}
                      onClick={() => {
                        setRoleFilter(opt.value);
                        setRoleOpen(false);
                      }}
                    >
                      {opt.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Trạng thái */}
            <div className="filter-dropdown">
              <button
                type="button"
                className="dropdown-trigger"
                onClick={() => setStatusOpen((v) => !v)}
              >
                {statusOptions.find((o) => o.value === statusFilter)?.label ||
                  "Tất cả trạng thái"}
                <span className="dropdown-arrow">▼</span>
              </button>
              {statusOpen && (
                <div className="dropdown-menu">
                  {statusOptions.map((opt) => (
                    <div
                      key={opt.value}
                      className={`dropdown-item${
                        statusFilter === opt.value ? " active" : ""
                      }`}
                      onClick={() => {
                        setStatusFilter(opt.value);
                        setStatusOpen(false);
                      }}
                    >
                      {opt.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Gói dịch vụ */}
            <div className="filter-dropdown">
              <button
                type="button"
                className="dropdown-trigger"
                onClick={() => setPackageOpen((v) => !v)}
              >
                {packageOptions.find((o) => o.value === packageFilter)?.label ||
                  "Tất cả gói"}
                <span className="dropdown-arrow">▼</span>
              </button>
              {packageOpen && (
                <div className="dropdown-menu">
                  {packageOptions.map((opt) => (
                    <div
                      key={opt.value}
                      className={`dropdown-item${
                        packageFilter === opt.value ? " active" : ""
                      }`}
                      onClick={() => {
                        setPackageFilter(opt.value);
                        setPackageOpen(false);
                      }}
                    >
                      {opt.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Danh sách người dùng */}
        <div className="users-list">
          <h3 className="list-title">Danh sách người dùng ({users.length})</h3>
          <p className="list-subtitle">Tất cả người dùng trong hệ thống</p>

          {loading && <div className="loading">Đang tải...</div>}
          {error && !loading && <div className="error">{error}</div>}

          <table className="users-table">
            <thead>
              <tr>
                <th>ID người dùng</th>
                <th>Người dùng</th>
                <th>Vai trò</th>
                <th>Gói dịch vụ</th>
                <th>Trạng thái</th>
                <th>Bài viết</th>
                <th>Ngày tham gia</th>
                <th>Đăng nhập cuối</th>
                <th>Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {!loading &&
                filteredUsers.map((u, i) => (
                  <tr key={i}>
                    <td>UM{u.id.toString().padStart(3, "0")}</td>
                    <td>
                      <div className="user-info">
                        <span className="user-name">{u.name}</span>
                        <span className="user-email">{u.email}</span>
                      </div>
                    </td>
                    <td>
                      <span
                        className={`badge role ${
                          u.role === "Admin" ? "admin" : "member"
                        }`}
                      >
                        {u.role}
                      </span>
                    </td>
                    <td>
                      {u.package ? (
                        <span
                          className={`badge package ${
                            u.package.includes("basic") ? "basic" : "super"
                          }`}
                        >
                          {u.package}
                        </span>
                      ) : (
                        <span>-</span>
                      )}
                    </td>
                    <td>
                      <span
                        className={`badge status ${
                          u.status === "Hoạt động" ? "active" : "locked"
                        }`}
                      >
                        {u.status}
                      </span>
                    </td>
                    <td>{u.posts}</td>
                    <td>{u.joined}</td>
                    <td>{u.lastLogin}</td>
                    <td style={{ position: "relative" }}>
                      <span
                        className="action-trigger"
                        onClick={() =>
                          setActionMenu(actionMenu === i ? null : i)
                        }
                      >
                        <MoreHorizontal size={18} />
                      </span>
                      {actionMenu === i && (
                        <div className="action-menu">
                          <div
                            className="action-item"
                            onClick={() => {
                              setPopup({ type: "detail", userIdx: i });
                              setActionMenu(null);
                            }}
                          >
                            <Eye size={18} className="action-icon" />
                            Xem chi tiết
                          </div>
                          <div
                            className="action-item"
                            onClick={() => {
                              setPopup({ type: "edit", userIdx: i });
                              setActionMenu(null);
                              // Khởi tạo form với dữ liệu người dùng hiện tại
                              const currentUser = users[i];
                              const currentRawUser = rawUsers[i];
                              initializeEditForm(currentUser, currentRawUser);
                            }}
                          >
                            <Edit2 size={18} className="action-icon" />
                            Chỉnh sửa
                          </div>
                          <div
                            className="action-item"
                            onClick={() => {
                              setPopup({ type: "history", userIdx: i });
                              setActionMenu(null);
                            }}
                          >
                            <Clock size={18} className="action-icon" />
                            Lịch sử giao dịch
                          </div>
                          <div
                            className="action-item lock"
                            onClick={() => {
                              handleToggleLock(i);
                              setActionMenu(null);
                            }}
                          >
                            <Lock size={18} className="action-icon" />
                            {rawUsers[i]?.isActive ? "Tạm khoá" : "Mở khoá"}
                          </div>
                          <div
                            className="action-item delete"
                            onClick={() => {
                              requestDelete(i);
                              setActionMenu(null);
                            }}
                          >
                            <Trash size={18} className="action-icon" />
                            Xoá tài khoản
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {renderPopup()}
        {confirmDeleteOpen && (
          <ConfirmDelete
            isOpen={confirmDeleteOpen}
            title="Xác nhận xoá tài khoản"
            message={
              <span>
                Bạn có chắc muốn xoá tài khoản này? Hành động không thể hoàn
                tác.
              </span>
            }
            confirmText="Xoá"
            cancelText="Hủy"
            isSubmitting={deleting}
            onConfirm={confirmDelete}
            onCancel={cancelDelete}
          />
        )}
        {statusChanging && (
          <div className="loading">Đang cập nhật trạng thái...</div>
        )}
        {notif && (
          <div className="success" style={{ marginTop: 12 }}>
            {notif}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Users;
